import { createInjectionState } from '@vueuse/core'
import { computed, reactive, watchEffect } from 'vue'

import { DEFAULT_LANG_VALUE, DEFAULT_MARKDOWN_THEME_VALUE } from '@/tiptap/constants/define'
import { useContext } from './use-context'
import { Node, Mark } from '@tiptap/core'

// --- Legacy compatibility shims (no-op renderers for old content) ---
// Add/rename shims to match any legacy node/mark names found in old JSON.
const LegacyParagraph = Node.create({
  name: 'paragraph_legacy',
  group: 'block',
  parseHTML: () => [{ tag: 'p' }],
  renderHTML: () => ['p', 0],
})

const LegacyBold = Mark.create({
  name: 'bold_legacy',
  parseHTML: () => [{ tag: 'strong' }, { style: 'font-weight=bold' }],
  renderHTML: () => ['strong', 0],
})

// Common legacy mark seen in older TipTap configs
const LegacyTextStyle = Mark.create({
  name: 'textStyle',
  parseHTML: () => [{ tag: 'span' }],
  renderHTML: () => ['span', 0],
})

// Helper: merge extensions and avoid duplicates by `name`
function mergeWithLegacy(base = []) {
  const legacy = [LegacyParagraph, LegacyBold, LegacyTextStyle]
  const seen = new Set(base.map(ext => ext?.name).filter(Boolean))
  const add = legacy.filter(ext => !seen.has(ext.name))
  return [...base, ...add]
}

// JS version of the Vuetify-Pro Tiptap store
// Provides: useProvideTiptapStore(), useTiptapStore()
export const [useProvideTiptapStore, useTiptapStore] = createInjectionState(() => {
  const { state: _state } = useContext()

  const state = reactive({
    // source-of-truth comes from use-context; mirror it here
    extensions: mergeWithLegacy(_state.extensions ?? []),
    defaultLang: DEFAULT_LANG_VALUE,
    defaultMarkdownTheme: DEFAULT_MARKDOWN_THEME_VALUE,
    isFullscreen: false,
    color: undefined,
    highlight: undefined,
  })

  const isFullscreen = computed(() => state.isFullscreen)

  function toggleFullscreen() {
    state.isFullscreen = !state.isFullscreen
  }

  // keep in sync with the global context store
  watchEffect(() => {
    state.extensions = mergeWithLegacy(_state.extensions)
    state.defaultLang = _state.defaultLang
    state.defaultMarkdownTheme = _state.defaultMarkdownTheme
  })

  return {
    state,
    isFullscreen,
    toggleFullscreen,
  }
})